/**
 * CR-6435 / CR-6436: the Calendar filter body for both portal views, with the
 * same section shell (`FilterSection` + divider) and control density
 * (`deliveryFilterStyles`) as the Deliveries panel, the accepted reference for
 * every filter rework.
 *
 * Customer view (CR-6435): Company · Supplier · Classification, then the
 * Event Types chips. Every section sits on the same four-column grid the ASN
 * and Deliveries panels use, so a control is one column wide whether its row
 * holds two fields or four; Supplier takes a second row for Supplier Type and
 * the Strategic flag. The Strategic flag is the design's All / Yes / No toggle
 * because the Calendar had no such control before.
 *
 * Supplier view (CR-6436): Company (the customers the supplier serves) ·
 * Supplier (Supplier Country, Supplier Site — the supplier's own sites) ·
 * Classification, which the design fills with the Event Types chips.
 *
 * Every control keeps the type the Deliveries panel uses for the same field.
 * Neither view carries the design's Warehouse box: calendar events carry no
 * warehouse, so the control could never change the result set.
 */
import { Building2, Truck, Tags } from 'lucide-react';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { Badge } from '../../components/ui/badge';
import { FilterSection } from '../../components/filters/FilterSection';
import { countryDisplayNames } from '../../components/dashboard/dashboardFilterUtils';
import { inputCls, labelCls, gridCls } from '../../components/purchasing/tabs/delivery/deliveryFilterStyles';
import { EVENT_TYPE_BG_COLORS, EVENT_TYPE_COLORS, EVENT_TYPE_LABELS } from './constants';
import type { CalendarFiltersState } from './calendarFilterParams';
import type { CalendarFilterOptions } from './useCalendarFilters';
import { useFilterVisibility } from '../../hooks/useFilterVisibility';

/** Horizontal divider with padding so it doesn't touch card edges */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

interface Option { value: string; label: string }

const STRATEGIC_CHOICES: ReadonlyArray<{ value: CalendarFiltersState['strategic']; label: string }> = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

export type CalendarFilterVariant = 'customer' | 'supplier';

export interface CalendarFilterSectionsProps {
  draft: CalendarFiltersState;
  onChange: <K extends keyof CalendarFiltersState>(key: K, value: CalendarFiltersState[K]) => void;
  options: CalendarFilterOptions;
  activeEventTypes: string[];
  onEventTypeToggle: (type: string) => void;
  /** Which design to render; defaults to the customer view. */
  variant?: CalendarFilterVariant;
  /** Types never generated for this viewer (e.g. audit actions for a supplier), so no chip. */
  hiddenEventTypes?: string[];
}

export function CalendarFilterSections({
  draft, onChange, options, activeEventTypes, onEventTypeToggle, variant = 'customer', hiddenEventTypes = [],
}: CalendarFilterSectionsProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  const selectField = (
    label: string,
    key: keyof CalendarFiltersState,
    choices: ReadonlyArray<Option>,
    allLabel: string,
  ) => (
    <div>
      <label className={labelCls}>{label}</label>
      <select
        value={draft[key]}
        onChange={(e) => onChange(key, e.target.value as CalendarFiltersState[typeof key])}
        className={inputCls}
      >
        <option value="">{allLabel}</option>
        {choices.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );

  const countrySelect = (label: string, key: 'companyCountry' | 'supplierCountry', values: string[]) =>
    selectField(label, key, values.map((c) => ({ value: c, label: countryDisplayNames[c] || c })), 'All Countries');

  const searchableField = (
    label: string,
    key: 'division' | 'site' | 'supplierGroup' | 'supplier' | 'commodityGroup',
    choices: ReadonlyArray<Option>,
    placeholder: string,
  ) => (
    <div>
      <label className={labelCls}>{label}</label>
      <SearchableSelect
        options={choices.map((o) => ({ value: o.value, label: o.label }))}
        value={draft[key]}
        onChange={(val) => onChange(key, val)}
        placeholder={placeholder}
      />
    </div>
  );

  // Company — Division and Country both narrow Site. Identical in both views;
  // in the supplier view the sites are the customers the supplier serves.
  // Every section uses the Deliveries / ASN four-column grid so a control keeps
  // the same width whether its row holds two fields or four.
  const companySection = (
    <FilterSection title="Company" icon={Building2}>
      <div className={gridCls}>
        {fv.division && searchableField('Division', 'division', options.divisions, 'All Divisions')}
        {fv.companyCountry && countrySelect('Country', 'companyCountry', options.companyCountries)}
        {searchableField('Site', 'site', options.sites, 'All Sites')}
      </div>
    </FilterSection>
  );

  // Event Types — every known type is offered, not only those present on the
  // current page, so a chip is always there to switch on. Toggles apply
  // immediately, as the badges always have.
  const eventTypes = (
    <div>
      <label className={labelCls}>Event Types</label>
      <div className="flex flex-wrap gap-2">
        {Object.entries(EVENT_TYPE_LABELS).filter(([type]) => !hiddenEventTypes.includes(type)).map(([type, label]) => {
          const isActive = activeEventTypes.includes(type);
          return (
            // CR-5459 rework: an active chip is filled with its own event
            // colour, so the chip row is the colour key. A blue chip used to
            // hide the dot of every blue event type.
            <Badge
              key={type}
              variant="outline"
              aria-pressed={isActive}
              className={`cursor-pointer hover:opacity-80 transition-opacity ${isActive ? 'border-transparent text-white' : 'bg-secondary text-muted-foreground'}`}
              style={isActive ? { backgroundColor: EVENT_TYPE_BG_COLORS[type] } : undefined}
              onClick={() => onEventTypeToggle(type)}
            >
              {!isActive && <div className={`w-2 h-2 rounded-full mr-1.5 ${EVENT_TYPE_COLORS[type]}`} />}
              {label}
            </Badge>
          );
        })}
      </div>
    </div>
  );

  if (variant === 'supplier') {
    return (
      <div className="[&>section]:border-t-0">
        <SectionDivider />
        {companySection}
        <SectionDivider />
        {/* Supplier — Site and Supplier Country narrow Supplier Site. */}
        <FilterSection title="Supplier" icon={Truck}>
          <div className={gridCls}>
            {countrySelect('Supplier Country', 'supplierCountry', options.supplierCountries)}
            {searchableField('Supplier Site', 'supplier', options.suppliers, 'All Supplier Sites')}
          </div>
        </FilterSection>
        <SectionDivider />
        {/* Classification — the supplier design puts the Event Types here. */}
        <FilterSection title="Classification" icon={Tags}>
          {eventTypes}
        </FilterSection>
      </div>
    );
  }

  return (
    <div className="[&>section]:border-t-0">
      {/* Divider above first section */}
      <SectionDivider />

      {companySection}

      <SectionDivider />

      {/* Supplier — Site, Supplier Group and Supplier Country narrow Supplier Site. */}
      <FilterSection title="Supplier" icon={Truck}>
        <div className={gridCls}>
          {searchableField('Supplier Group', 'supplierGroup', options.supplierGroups, 'All Supplier Groups')}
          {countrySelect('Supplier Country', 'supplierCountry', options.supplierCountries)}
          {searchableField('Supplier Site', 'supplier', options.suppliers, 'All Supplier Sites')}
          {fv.supplierCode && (
          <div>
            <label className={labelCls}>Supplier Code</label>
            <input
              type="text"
              placeholder="Supplier code..."
              value={draft.supplierCode}
              onChange={(e) => onChange('supplierCode', e.target.value)}
              className={inputCls}
            />
          </div>
          )}
        </div>
        <div className={`${gridCls} mt-3`}>
          {fv.supplierType && selectField('Supplier Type', 'supplierType', [
            { value: 'internal', label: 'Internal' },
            { value: 'external', label: 'External' },
          ], 'All Suppliers')}
          <div>
            <label className={labelCls}>Strategic Flag</label>
            <div className="flex" role="group" aria-label="Strategic Flag">
              {STRATEGIC_CHOICES.map(({ value, label }, i) => {
                const active = draft.strategic === value;
                const rounded = i === 0 ? 'rounded-l-md' : i === STRATEGIC_CHOICES.length - 1 ? 'rounded-r-md' : '';
                return (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onChange('strategic', value)}
                    className={`px-4 py-2 text-sm font-medium border transition-colors ${rounded} ${
                      active
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-muted-foreground border-border hover:bg-muted'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </FilterSection>

      {/* CR-4802: the whole group is tenant-gated; drop the heading when nothing is shown. */}
      {(fv.commodity) && (<>
        <SectionDivider />

        {/* Classification — Commodity Group narrows Commodity Type. */}
        <FilterSection title="Classification" icon={Tags}>
          <div className={gridCls}>
            {fv.commodity && searchableField('Commodity Group', 'commodityGroup', options.commodityGroups, 'All Commodity Groups')}
            {fv.commodity && selectField('Commodity Type', 'commodityType', options.commodityTypes, 'All Commodity Types')}
          </div>
        </FilterSection>
      </>)}

      <div className="px-6 py-4">{eventTypes}</div>
    </div>
  );
}
